import cuid from "cuid";
import {
  get,
  groupBy,
  isEmpty,
  isEqual,
  isNil,
  keyBy,
  orderBy,
  sortBy,
  uniq,
} from "lodash";
import { EDGE_KINDS, NODE_TYPES } from "utils/constants";
import { SCREEN_VERSION_STATUS } from "utils/helpers/screenDraft";
import { fieldOperandOf, operationSymbol } from "./catalog";

const STATUS_FIELDS = [
  "name",
  "tag",
  "color",
  "background",
  "progress",
  "order",
  "template",
  "moveToNext",
];

const hasPosition = (position) =>
  !isNil(position) && !isNil(get(position, "x")) && !isNil(get(position, "y"));

const pickStatusData = (status) =>
  STATUS_FIELDS.reduce((data, field) => {
    data[field] = get(status, field);
    return data;
  }, {});

export const conditionSummary = (conditions = []) =>
  conditions
    .filter((condition) => !isNil(condition))
    .map((condition) =>
      [
        get(condition, "leftOperand.accessor"),
        operationSymbol(get(condition, "operation")),
        fieldOperandOf(condition) ?? get(condition, "rightOperand.value"),
      ]
        .filter((part) => !isNil(part) && part !== "")
        .join(" "),
    )
    .filter((part) => part !== "")
    .join(" · ");

export const edgeSummary = (possibleStatus = {}) => {
  const kind = possibleStatus.kind || EDGE_KINDS.CONDITION;
  if (kind === EDGE_KINDS.EVENT)
    return possibleStatus.event ? `⚡ ${possibleStatus.event}` : "⚡";
  if (kind === EDGE_KINDS.TIMER)
    return isNil(possibleStatus.delaySeconds)
      ? "⏱"
      : `⏱ ${possibleStatus.delaySeconds}s`;
  if (kind === EDGE_KINDS.ALWAYS) return "→";
  return conditionSummary(possibleStatus.conditions);
};

const ROUTING_KINDS = [EDGE_KINDS.CONDITION, EDGE_KINDS.ALWAYS];

export const isRoutingEdge = (edge) =>
  ROUTING_KINDS.includes(get(edge, "data.kind") || EDGE_KINDS.CONDITION);

const edgeOrder = (edge) => Number(get(edge, "data.order")) || 0;

export const nextEdgeOrder = (edges = [], source) =>
  edges
    .filter((edge) => edge.source === source && isRoutingEdge(edge))
    .reduce((max, edge) => Math.max(max, edgeOrder(edge)), 0) + 1;

export const newRuleEdge = ({ connection, id, edges = [] }) => ({
  ...connection,
  id,
  label: "",
  data: {
    possibleStatusId: cuid(),
    order: nextEdgeOrder(edges, connection.source),
    kind: EDGE_KINDS.CONDITION,
    event: "",
    delaySeconds: null,
    conditions: [],
  },
});

export const newStatusNode = ({ id, name, nodeType, order, position }) => ({
  id,
  type: "status",
  position,
  data: {
    name,
    tag: undefined,
    color: "",
    background: "",
    progress: "",
    order,
    template: "",
    moveToNext: false,
    nodeType,
    config: {},
    actions: [],
    formDefinition: null,
    entryConnectors: [],
    outputConnectors: [],
    dataBindings: [],
    screenCount: 0,
    screenPublished: false,
    screenId: null,
    screenVersionId: null,
    screenVersionStatus: null,
    screenHtml: null,
    screenReport: null,
  },
});

export const rankEdgeLabels = (edges = []) => {
  const routingBySource = groupBy(edges.filter(isRoutingEdge), "source");
  return edges.map((edge) => {
    const siblings = routingBySource[edge.source] || [];
    if (!isRoutingEdge(edge) || siblings.length < 2) return edge;
    const orders = sortBy(uniq(siblings.map(edgeOrder)));
    const rank = orders.indexOf(edgeOrder(edge)) + 1;
    return {
      ...edge,
      label: edge.label ? `${rank} · ${edge.label}` : String(rank),
    };
  });
};

const latestVersion = (versions = []) =>
  versions.reduce(
    (latest, version) =>
      isNil(latest) ||
      Number(get(version, "versionNumber")) >
        Number(get(latest, "versionNumber"))
        ? version
        : latest,
    null,
  );

// Admin canvas prefers the latest version so draft HTML edits show before
// publish. Runtime (CanvasRunner) still reads currentPublishedVersion only.
const previewVersion = (screen) =>
  latestVersion(screen.versions) || screen.currentPublishedVersion;

const isPublishedScreen = (screen) =>
  !isNil(get(screen, "currentPublishedVersion.id"));

const draftUpdatedTime = (screen) => {
  const time = Date.parse(get(latestVersion(screen.versions), "updatedDate"));
  return Number.isNaN(time) ? 0 : time;
};

const DRAFT_SCREEN_RANK = [isPublishedScreen, draftUpdatedTime, "id"];
const DRAFT_SCREEN_ORDER = ["desc", "desc", "asc"];

const hasHtml = (screen) =>
  !isEmpty(get(previewVersion(screen), "htmlContent"));

const previewScreen = (screens = []) => {
  const drafts = screens.filter(hasDraftVersion);
  if (drafts.length > 0)
    return orderBy(drafts, DRAFT_SCREEN_RANK, DRAFT_SCREEN_ORDER)[0];
  return (
    screens.find(isPublishedScreen) ||
    screens.find(hasHtml) ||
    screens[0] ||
    null
  );
};

const slotsOf = (version) =>
  Array.isArray(get(version, "bindingManifest.slots"))
    ? get(version, "bindingManifest.slots")
    : [];

const screenData = (screens = []) => {
  const screen = previewScreen(screens);
  const version = screen ? previewVersion(screen) : null;
  const published = get(screen, "currentPublishedVersion") || null;
  return {
    screenCount: screens.length,
    screenPublished: screens.some(isPublishedScreen),
    screenId: screen ? screen.id : null,
    screenPublishedVersion:
      get(screen, "currentPublishedVersion.versionNumber") || null,
    screenVersionId: get(version, "id") || null,
    screenVersionNumber: get(version, "versionNumber") || null,
    screenVersionStatus: get(version, "status") || null,
    screenHtml: get(version, "htmlContent") || null,
    screenStyleContent: get(version, "styleContent") || null,
    screenSlots: slotsOf(version),
    screenManifestId: get(version, "bindingManifest.id") || null,
    screenReport: get(version, "bindingManifest.report") || null,
    screenStates: get(version, "states") || [],
    screenPublishedSlots: slotsOf(published),
    screenPublishedStates: get(published, "states") || [],
  };
};

export const hasDraftVersion = (screen) => {
  const latest = latestVersion(get(screen, "versions") || []);
  if (get(latest, "status") !== SCREEN_VERSION_STATUS.DRAFT) return false;
  const published = get(screen, "currentPublishedVersion.versionNumber");
  return (
    isNil(published) || Number(get(latest, "versionNumber")) > Number(published)
  );
};

export const stepScreens = ({ process, draft }) => {
  const byStep = {};
  for (const status of get(process, "processStatuses") || [])
    byStep[status.id] = status.screens || [];
  for (const screen of get(draft, "screens") || []) {
    if (isNil(screen.draftStatusId)) continue;
    byStep[screen.draftStatusId] = [
      ...(byStep[screen.draftStatusId] || []),
      screen,
    ];
  }
  return byStep;
};

export const draftScreenCount = ({ process, draft }) =>
  Object.values(stepScreens({ process, draft })).flat().filter(hasDraftVersion)
    .length;

export const draftScreenStepIds = ({ process, draft }) =>
  uniq(
    Object.entries(stepScreens({ process, draft }))
      .filter(([, screens]) => (screens || []).some(hasDraftVersion))
      .map(([stepId]) => stepId),
  );

export const draftStatuses = ({ process, draft }) => {
  const screens = stepScreens({ process, draft });
  const statuses = get(draft, "graph.processStatuses");
  return (
    Array.isArray(statuses) ? statuses : get(process, "processStatuses") || []
  ).map((status) => ({ ...status, screens: screens[status.id] || [] }));
};

export const withScreenData = (nodes = [], screensByStep = {}) => {
  let changed = false;
  const next = nodes.map((node) => {
    const fresh = screenData(screensByStep[node.id] || []);
    const same = Object.keys(fresh).every((key) =>
      isEqual(fresh[key], get(node, ["data", key])),
    );
    if (same) return node;
    changed = true;
    return { ...node, data: { ...node.data, ...fresh } };
  });
  return changed ? next : nodes;
};

export const withSharedScreenNames = (nodes = []) => {
  const nameById = nodes.reduce((names, node) => {
    names[node.id] = get(node, "data.name");
    return names;
  }, {});
  return nodes.map((node) => {
    const name = nameById[get(node, "data.config.screenFrom")] || null;
    return name === get(node, "data.screenFromName")
      ? node
      : { ...node, data: { ...node.data, screenFromName: name } };
  });
};

export const toGraph = (processStatuses = []) => {
  const statusNameById = processStatuses.reduce((names, status) => {
    names[status.id] = status.name;
    return names;
  }, {});
  const nodes = processStatuses.map((status) => ({
    id: status.id,
    type: "status",
    position: hasPosition(status.position) ? status.position : { x: 0, y: 0 },
    data: {
      ...pickStatusData(status),
      nodeType: status.nodeType || NODE_TYPES.STATUS,
      config: status.config || {},
      actions: status.actions || [],
      screenFromName: statusNameById[get(status, "config.screenFrom")] || null,
      formDefinition: status.formDefinition || null,
      entryConnectors: status.entryConnectors || [],
      outputConnectors: status.outputConnectors || [],
      dataBindings: status.dataBindings || [],
      ...screenData(status.screens || []),
    },
  }));

  const edges = processStatuses.flatMap((status) =>
    (status.possibleStatuses || [])
      .filter((possibleStatus) => !isNil(possibleStatus.nextStatus))
      .map((possibleStatus) => ({
        id: possibleStatus.id || `${status.id}->${possibleStatus.nextStatus}`,
        source: status.id,
        target: possibleStatus.nextStatus,
        label: edgeSummary(possibleStatus),
        data: {
          possibleStatusId: possibleStatus.id,
          order: possibleStatus.order,
          kind: possibleStatus.kind || EDGE_KINDS.CONDITION,
          event: possibleStatus.event || "",
          delaySeconds: isNil(possibleStatus.delaySeconds)
            ? null
            : possibleStatus.delaySeconds,
          conditions: possibleStatus.conditions || [],
        },
      })),
  );

  return { nodes, edges };
};

export const fromGraph = (nodes = [], edges = [], baseStatuses = []) => {
  const baseById = keyBy(baseStatuses, "id");
  const edgesBySource = edges.reduce((map, edge) => {
    (map[edge.source] = map[edge.source] || []).push(edge);
    return map;
  }, {});

  return nodes.map((node) => {
    const base = baseById[node.id] || {};
    return {
      id: node.id,
      ...pickStatusData(node.data),
      nodeType: get(node, "data.nodeType") || NODE_TYPES.STATUS,
      config: get(node, "data.config") || {},
      formDefinition: get(node, "data.formDefinition") || null,
      entryConnectors: get(node, "data.entryConnectors") || [],
      outputConnectors: get(node, "data.outputConnectors") || [],
      dataBindings: get(node, "data.dataBindings") || [],
      position: hasPosition(node.position) ? node.position : { x: 0, y: 0 },
      possibleStatuses: (edgesBySource[node.id] || []).map((edge) => ({
        id: get(edge, "data.possibleStatusId") || edge.id,
        nextStatus: edge.target,
        order: get(edge, "data.order"),
        kind: get(edge, "data.kind") || EDGE_KINDS.CONDITION,
        event: get(edge, "data.event") || null,
        delaySeconds: isNil(get(edge, "data.delaySeconds"))
          ? null
          : Number(get(edge, "data.delaySeconds")),
        conditions: get(edge, "data.conditions") || [],
      })),
      // The panel edits mirror statuses in place; nodes never opened keep the
      // actions they were loaded with.
      actions: get(node, "data.actions") || base.actions || [],
    };
  });
};
